import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.jsx';
import { menuTree, routeForSlug } from '../menuData.js';
import HelpDrawer from '../../help/HelpDrawer.jsx';
import { lookupHelpForPathname } from '../../help/helpContent.js';

/**
 * Shared top navbar + menu + account chrome, reused across every
 * "default web" layout variant (_default-web-layout / -1ghj3iy / -1r2bgmj —
 * three raw shells that are visually/functionally identical). Renders the
 * navigation from `menuTree` (routing by slug, never by page id) and the
 * session chrome required by create-frontend-layout-components policy 9:
 * logged-in user detail + logout when authenticated, a login affordance when
 * not. Also hosts the `?` in-app help affordance (maintain-in-app-help-docs)
 * next to the account chrome.
 */
export default function AppNavbar() {
  const { user, isAuthenticated, logout } = useAuth();
  const location = useLocation();
  const [showHelp, setShowHelp] = useState(false);
  const helpLookup = lookupHelpForPathname(location.pathname);

  return (
    <nav className="navbar navbar-expand-xxl navbar-dark bg-primary text-light fixed-top">
      <div className="container-fluid">
        <Link className="navbar-brand" to="/">
          MarginOS
        </Link>

        <button
          className="navbar-toggler"
          type="button"
          data-bs-toggle="offcanvas"
          data-bs-target="#navbarNav"
          aria-controls="navbarNav"
          aria-label="Toggle navigation"
        >
          <span className="navbar-toggler-icon"></span>
        </button>

        {/* Below xxl the menu moves off the top bar and into a left drawer
            instead of wrapping or clipping. Bootstrap renders this inline as a
            normal horizontal navbar once the viewport is wide enough to hold
            all six groups, so the drawer only exists when it is needed. */}
        <div
          className="offcanvas offcanvas-start text-bg-primary"
          tabIndex="-1"
          id="navbarNav"
          aria-labelledby="navbarNavLabel"
        >
          <div className="offcanvas-header">
            <h5 className="offcanvas-title" id="navbarNavLabel">
              MarginOS
            </h5>
            <button
              type="button"
              className="btn-close btn-close-white"
              data-bs-dismiss="offcanvas"
              aria-label="Close menu"
            ></button>
          </div>
          <div className="offcanvas-body">
          <ul className="navbar-nav">
            {menuTree.map((group) => (
              <li className="nav-item dropdown" key={group.label}>
                <a
                  className="nav-link text-white dropdown-toggle"
                  href="#"
                  id={`mainMenu-${sanitizeId(group.label)}`}
                  role="button"
                  data-bs-toggle="dropdown"
                  aria-haspopup="true"
                  aria-expanded="false"
                >
                  {group.icon ? <i className={`${group.icon} me-1`} aria-hidden="true"></i> : null}
                  {group.label}
                </a>
                <ul className="dropdown-menu" aria-labelledby={`mainMenu-${sanitizeId(group.label)}`}>
                  {group.children.map((item) => (
                    <li key={item.slug}>
                      <Link className="dropdown-item" to={routeForSlug(item.slug)}>
                        {item.icon ? <i className={`${item.icon} me-2`} aria-hidden="true"></i> : null}
                        {item.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
          </div>
        </div>

        <div className="ms-auto d-flex align-items-center">
          <button
            type="button"
            className="btn btn-outline-light btn-sm rounded-circle me-2"
            aria-label="Open help"
            title="Help"
            onClick={() => setShowHelp(true)}
          >
            <i className="fa fa-question" aria-hidden="true"></i>
          </button>

          {isAuthenticated ? (
            <div className="dropdown">
              <button
                className="btn btn-link text-white text-decoration-none dropdown-toggle d-flex align-items-center"
                type="button"
                id="accountMenu"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                aria-label="Account menu"
              >
                <span className="me-2">Hello, {user?.name || user?.email || 'User'}!</span>
                <img
                  src={`https://placehold.co/30?text=${encodeURIComponent((user?.name || user?.email || 'U').charAt(0).toUpperCase())}`}
                  alt="User avatar"
                  className="rounded-circle"
                  width={30}
                  height={30}
                />
              </button>
              <ul className="dropdown-menu dropdown-menu-end" aria-labelledby="accountMenu">
                <li>
                  <span className="dropdown-item-text text-muted small">{user?.email}</span>
                </li>
                <li>
                  <hr className="dropdown-divider" />
                </li>
                <li>
                  <button className="dropdown-item" type="button" onClick={logout}>
                    <i className="fa fa-sign-out-alt me-2" aria-hidden="true"></i>
                    Logout
                  </button>
                </li>
              </ul>
            </div>
          ) : (
            <Link className="btn btn-outline-light btn-sm" to="/login">
              <i className="fa fa-sign-in-alt me-1" aria-hidden="true"></i>
              Login
            </Link>
          )}
        </div>
      </div>

      <HelpDrawer
        show={showHelp}
        onClose={() => setShowHelp(false)}
        initialSectionId={helpLookup?.sectionId}
        initialTopicId={helpLookup?.topicId}
      />
    </nav>
  );
}

function sanitizeId(label) {
  return label.replace(/\W+/g, '_');
}
